import { MAX_REPORTING_RANGE_DAYS } from "@adpilot/shared";
import { IconCalendar, IconChevronDown, type TablerIcon } from "@tabler/icons-react";
import {
  addDays,
  differenceInCalendarDays,
  format,
  isLastDayOfMonth,
  parseISO,
  subDays,
} from "date-fns";
import { enUS, zhCN } from "date-fns/locale";
import { useState } from "react";
import type { DateRange, Matcher } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverPrimitive,
  PopoverTitle,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SegmentControl, SegmentControlItem } from "@/components/ui/segment-control";
import { Separator } from "@/components/ui/separator";
import { type ReportingTimeZone, reportingDateInTimeZone } from "@/lib/reporting-time-zone";
import { cn } from "@/lib/utils";
import {
  REPORTING_PERIOD_PRESETS,
  type ReportingPeriod,
  type ReportingPeriodPreset,
  type ResolvedReportingPeriod,
  resolveReportingPeriod,
  TOOLBAR_PERIOD_PRESETS,
} from "@/stores/reporting-period";

/**
 * Optional comparison mode edited in the popup footer and committed with Apply
 * (toolbar variant only). The caller owns the modes and which ones are available.
 */
export interface ReportingPeriodCompare<C extends string = string> {
  value: C;
  onValueChange: (value: C) => void;
  options: readonly { value: C; label: string; disabled?: boolean }[];
  /** Footer label in front of the mode switch, e.g. "Compare". */
  label: string;
  /** Range the mode compares against, drawn as the neutral band; null when off. */
  resolveRange: (range: ResolvedReportingPeriod, mode: C) => ResolvedReportingPeriod | null;
  /** Footer line for the compared range, e.g. `vs. ${range}`. */
  describe: (formattedRange: string) => string;
}

interface ReportingPeriodSelectProps<C extends string = string> {
  value: ReportingPeriod;
  timeZone: ReportingTimeZone;
  onValueChange: (value: ReportingPeriod) => void;
  /**
   * `default` keeps the compact list-page picker; `toolbar` is the dashboard filter
   * bar picker (bordered trigger with the applied range, preset rail, two months,
   * range summary and optional compare footer).
   */
  variant?: "default" | "toolbar";
  /** Toolbar variant: comparison mode edited alongside the range. */
  compare?: ReportingPeriodCompare<C>;
  /** Toolbar variant: popup edge aligned to the trigger. */
  align?: "start" | "end";
  /** Toolbar variant: rail entry that marks a manual calendar pick (never clickable). */
  customPresetLabel?: string;
}

/**
 * Bordered filter trigger shared by the dashboard filter bar: rest control-default,
 * hover control-hover + control-border-hover, open keeps the hover border on
 * control-open (never heavier than hover), focus ring + halo, disabled subtle fill.
 */
export const reportingFilterTriggerClassName =
  "group/trigger inline-flex h-8 max-w-full shrink-0 cursor-pointer items-center gap-1.5 rounded-sm border border-control-border bg-control-default pr-2 pl-2.5 text-sm font-medium text-ink outline-none select-none transition-[background-color,border-color,box-shadow] duration-[var(--duration-fast)] hover:border-control-border-hover hover:bg-control-hover focus-visible:border-focus-ring focus-visible:ring-2 focus-visible:ring-focus-halo data-popup-open:border-control-border-hover data-popup-open:bg-control-open disabled:pointer-events-none disabled:border-line disabled:bg-subtle disabled:opacity-60";

/** 14px Tabler icon slot used inside `reportingFilterTriggerClassName` triggers. */
export function FilterTriggerIcon({
  icon: Icon,
  chevron = false,
}: {
  icon: TablerIcon;
  chevron?: boolean;
}): React.ReactElement {
  return (
    <span aria-hidden className="flex size-3.5 shrink-0 items-center justify-center">
      <Icon
        className={cn(
          "size-3.5 text-muted-foreground",
          chevron &&
            "transition-transform duration-[var(--duration-fast)] group-data-popup-open/trigger:rotate-180",
        )}
        stroke={1.75}
      />
    </span>
  );
}

/** Same-length window immediately before `range` (the "previous period" comparison). */
export function previousReportingPeriod(range: ResolvedReportingPeriod): ResolvedReportingPeriod {
  const days = differenceInCalendarDays(parseISO(range.endDate), parseISO(range.startDate)) + 1;
  return {
    startDate: toIsoDate(subDays(parseISO(range.startDate), days)),
    endDate: toIsoDate(subDays(parseISO(range.startDate), 1)),
  };
}

function toDateRange(period: ReportingPeriod, timeZone: ReportingTimeZone): DateRange {
  const resolved = resolveReportingPeriod(period, new Date(), timeZone);
  return { from: parseISO(resolved.startDate), to: parseISO(resolved.endDate) };
}

function toIsoDate(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

/**
 * Days the calendar must grey out so a selection can never exceed the span the
 * API accepts. Without this the picker happily builds an over-long window and
 * every panel drops to ErrorState on the resulting 422, with no hint as to why.
 *
 * Deliberately three independent matchers rather than one `{ before, after }`:
 * react-day-picker reads that pair as a DateInterval whose meaning flips on
 * operand order (`isAfter(before, after)` picks "inside" vs "outside"), so a
 * combined matcher would silently stop disabling anything if the operands ever
 * crossed — with no type error and no failing render to catch it.
 */
export function outOfRangeDays(anchor: Date | undefined, today: Date): Matcher[] {
  if (!anchor) return [{ after: today }];

  const span = MAX_REPORTING_RANGE_DAYS - 1;
  return [{ before: subDays(anchor, span) }, { after: today }, { after: addDays(anchor, span) }];
}

export function ReportingPeriodSelect<C extends string = string>({
  value,
  timeZone,
  onValueChange,
  variant = "default",
  compare,
  align = "end",
  customPresetLabel,
}: ReportingPeriodSelectProps<C>): React.ReactElement {
  if (variant === "toolbar") {
    return (
      <ToolbarPeriodPicker
        value={value}
        timeZone={timeZone}
        onValueChange={onValueChange}
        compare={compare}
        align={align}
        customPresetLabel={customPresetLabel}
      />
    );
  }
  return <DefaultPeriodPicker value={value} timeZone={timeZone} onValueChange={onValueChange} />;
}

function DefaultPeriodPicker({
  value,
  timeZone,
  onValueChange,
}: Pick<ReportingPeriodSelectProps, "value" | "timeZone" | "onValueChange">): React.ReactElement {
  const { t, i18n } = useTranslation("layout");
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange>(() => toDateRange(value, timeZone));
  const [draftPreset, setDraftPreset] = useState<ReportingPeriodPreset | null>(() =>
    value.type === "preset" ? value.value : null,
  );
  const locale = i18n.resolvedLanguage === "zh-CN" ? zhCN : enUS;
  const today = parseISO(reportingDateInTimeZone(new Date(), timeZone));

  const presetLabels = usePresetLabels();

  function resetDraft(): void {
    setDraft(toDateRange(value, timeZone));
    setDraftPreset(value.type === "preset" ? value.value : null);
  }

  function handleOpenChange(nextOpen: boolean): void {
    if (nextOpen) resetDraft();
    setOpen(nextOpen);
  }

  function selectPreset(preset: ReportingPeriodPreset): void {
    setDraft(toDateRange({ type: "preset", value: preset }, timeZone));
    setDraftPreset(preset);
  }

  function applyDraft(): void {
    if (!draft.from || !draft.to) return;

    onValueChange(
      draftPreset
        ? { type: "preset", value: draftPreset }
        : { type: "custom", startDate: toIsoDate(draft.from), endDate: toIsoDate(draft.to) },
    );
    setOpen(false);
  }

  function selectCalendarDay(day: Date): void {
    setDraftPreset(null);
    if (!draft.from || draft.to || day < draft.from) {
      setDraft({ from: day, to: undefined });
      return;
    }

    setDraft({ from: draft.from, to: day });
  }

  const selectingEnd = Boolean(draft.from && !draft.to);

  // While an end date is still pending, the start date anchors the allowed span.
  const disabledDays = outOfRangeDays(draft.from && !draft.to ? draft.from : undefined, today);

  const appliedRange = toDateRange(value, timeZone);
  const displayFormat = new Intl.DateTimeFormat(i18n.resolvedLanguage, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const formatBoundary = (date: Date, boundary: "start" | "end"): string =>
    `${displayFormat.format(date)} ${boundary === "start" ? "00:00:00" : "23:59:59"}`;
  const appliedLabel =
    appliedRange.from && appliedRange.to
      ? `${displayFormat.format(appliedRange.from)} – ${displayFormat.format(appliedRange.to)}`
      : t("reportingPeriod.title");
  const triggerLabel = value.type === "preset" ? presetLabels[value.value] : appliedLabel;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        render={
          <Button
            variant="outline"
            size="sm"
            aria-label={`${t("reportingPeriod.title")}: ${appliedLabel}`}
            title={appliedLabel}
            className="w-[140px] justify-between"
          />
        }
      >
        <IconCalendar data-icon="inline-start" />
        <span
          className={cn("min-w-0 truncate", value.type === "custom" && "font-mono tabular-nums")}
        >
          {triggerLabel}
        </span>
        <IconChevronDown data-icon="inline-end" className="ml-auto" />
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[760px] overflow-hidden rounded-[14px] p-0"
      >
        <div className="grid grid-cols-[156px_1px_minmax(0,1fr)]">
          <aside className="flex flex-col p-3">
            <span className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              {t("reportingPeriod.quickSelect")}
            </span>
            <div className="flex flex-col gap-1">
              {REPORTING_PERIOD_PRESETS.map((preset) => (
                <Button
                  key={preset}
                  type="button"
                  variant={draftPreset === preset ? "secondary" : "ghost"}
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => selectPreset(preset)}
                >
                  {presetLabels[preset]}
                </Button>
              ))}
            </div>
          </aside>

          <Separator orientation="vertical" />

          <section className="min-w-0">
            <div className="flex items-center justify-between px-5 pb-3 pt-4">
              <div>
                <PopoverTitle className="text-[14px]">
                  {t("reportingPeriod.currentPeriod")}
                </PopoverTitle>
                <PopoverDescription className="mt-1 text-[11px]">
                  {t("reportingPeriod.selectHint")}
                </PopoverDescription>
              </div>
              <div className="grid grid-cols-[132px_16px_132px] items-center">
                <DateValue
                  label={t("reportingPeriod.startDate")}
                  value={draft.from ? formatBoundary(draft.from, "start") : "—"}
                  active={!selectingEnd}
                />
                <span className="text-center text-muted-foreground">–</span>
                <DateValue
                  label={t("reportingPeriod.endDate")}
                  value={draft.to ? formatBoundary(draft.to, "end") : "—"}
                  active={selectingEnd}
                />
              </div>
            </div>

            <Separator />

            <Calendar
              mode="range"
              selected={draft}
              // onSelect keeps DayPicker controlled; its default range algorithm
              // must not maintain a second selection alongside our two-click draft.
              onSelect={(_range, day, modifiers) => {
                if (!modifiers.disabled) selectCalendarDay(day);
              }}
              numberOfMonths={2}
              defaultMonth={draft.from}
              disabled={disabledDays}
              locale={locale}
              className="mx-auto p-4 [--cell-size:--spacing(9)]"
              classNames={{ months: "gap-7", month: "w-[252px]" }}
            />

            <Separator />

            <footer className="flex items-center justify-end gap-2 px-4 py-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleOpenChange(false)}
              >
                {t("reportingPeriod.cancel")}
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={!draft.from || !draft.to}
                onClick={applyDraft}
              >
                {t("reportingPeriod.apply")}
              </Button>
            </footer>
          </section>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function DateValue({
  label,
  value,
  active,
}: {
  label: string;
  value: string;
  active: boolean;
}): React.ReactElement {
  return (
    <fieldset
      aria-label={label}
      aria-current={active ? "step" : undefined}
      className={cn(
        "m-0 flex h-14 min-w-0 flex-col justify-center rounded-[8px] border border-control-border bg-control-default px-2.5",
        active && "border-ring ring-1 ring-ring",
      )}
    >
      <span className="text-[9px] leading-3 text-muted-foreground">{label}</span>
      <span className="font-mono text-[11px] leading-4 tabular-nums text-ink">{value}</span>
    </fieldset>
  );
}

function usePresetLabels(): Record<ReportingPeriodPreset, string> {
  const { t } = useTranslation("layout");
  return {
    today: t("reportingPeriod.today"),
    yesterday: t("reportingPeriod.yesterday"),
    "7d": t("reportingPeriod.last7Days"),
    "14d": t("reportingPeriod.last14Days"),
    "30d": t("reportingPeriod.last30Days"),
    "90d": t("reportingPeriod.last90Days"),
    this_month: t("reportingPeriod.thisMonth"),
    last_month: t("reportingPeriod.lastMonth"),
    this_quarter: t("reportingPeriod.thisQuarter"),
    ytd: t("reportingPeriod.yearToDate"),
  };
}

/**
 * Paper date range picker: 32px day lanes with a 28px day inside, brand endpoints on an
 * info band, the compare range as a neutral band. Text sizes use `text-(length:…)` so
 * Calendar's tailwind-merge does not mistake the custom size tokens for colours.
 */
const TOOLBAR_CALENDAR_CLASS_NAMES = {
  months: "flex-row gap-6",
  month: "w-56",
  month_caption: "mx-0 mb-1 h-7 px-0",
  caption_label: "text-sm font-semibold tracking-tight text-ink",
  nav: "inset-x-0",
  button_previous:
    "size-7 rounded-xs text-muted-foreground not-in-data-selected:hover:bg-state-hover hover:bg-state-hover",
  button_next:
    "size-7 rounded-xs text-muted-foreground not-in-data-selected:hover:bg-state-hover hover:bg-state-hover",
  weekday:
    "h-6 w-8 text-(length:--text-micro) leading-(--text-micro--line-height) font-medium uppercase tracking-caps text-muted-foreground",
  day: "relative size-8 p-0 before:pointer-events-none before:absolute before:inset-x-0 before:inset-y-0.5 first:before:left-0.5 first:before:rounded-l-xs last:before:right-0.5 last:before:rounded-r-xs",
  day_button:
    "relative z-1 m-0.5 size-7 rounded-xs text-sm tabular-nums text-ink disabled:opacity-100 not-in-data-selected:hover:bg-state-hover in-data-selected:bg-brand in-data-selected:font-semibold in-data-selected:text-on-brand in-[.range-middle]:rounded-xs in-[.range-end:not(.range-start)]:rounded-s-xs in-[.range-start:not(.range-end)]:rounded-e-xs in-[.range-middle]:in-data-selected:bg-transparent in-[.range-middle]:in-data-selected:font-normal in-[.range-middle]:in-data-selected:text-ink in-[.range-middle]:hover:bg-state-hover in-data-disabled:text-placeholder in-data-disabled:no-underline in-data-outside:text-placeholder focus-visible:ring-focus-halo",
  range_start: "before:left-0.5 before:rounded-l-xs before:bg-info-background",
  range_middle: "before:bg-info-background",
  range_end: "before:right-0.5 before:rounded-r-xs before:bg-info-background",
  today:
    "*:after:bottom-[3px] *:after:size-1 *:after:bg-brand [&[data-selected]:not(.range-middle)>*]:after:bg-on-brand",
};

const TOOLBAR_CALENDAR_MODIFIER_CLASS_NAMES = {
  monthStart: "before:left-0.5 before:rounded-l-xs",
  monthEnd: "before:right-0.5 before:rounded-r-xs",
  compare: "before:bg-subtle",
  compareStart: "before:left-0.5 before:rounded-l-xs",
  compareEnd: "before:right-0.5 before:rounded-r-xs",
  compareEdge: "*:shadow-[inset_0_0_0_1px_var(--color-control-border-hover)]",
};

function ToolbarPeriodPicker<C extends string>({
  value,
  timeZone,
  onValueChange,
  compare,
  align,
  customPresetLabel,
}: Pick<ReportingPeriodSelectProps<C>, "value" | "timeZone" | "onValueChange" | "compare"> & {
  align: "start" | "end";
  customPresetLabel?: string;
}): React.ReactElement {
  const { t, i18n } = useTranslation("layout");
  const presetLabels = usePresetLabels();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange>(() => toDateRange(value, timeZone));
  const [draftPreset, setDraftPreset] = useState<ReportingPeriodPreset | null>(() =>
    value.type === "preset" ? value.value : null,
  );
  const [draftCompare, setDraftCompare] = useState<C | undefined>(compare?.value);
  const language = i18n.resolvedLanguage ?? "en-US";
  const locale = language === "zh-CN" ? zhCN : enUS;
  const today = parseISO(reportingDateInTimeZone(new Date(), timeZone));
  const rangeFormat = new Intl.DateTimeFormat(language, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const dayCountFormat = new Intl.NumberFormat(language, {
    style: "unit",
    unit: "day",
    unitDisplay: "long",
  });
  const formatRange = (from: Date, to: Date) => rangeFormat.formatRange(from, to);

  const applied = toDateRange(value, timeZone);
  const appliedLabel =
    applied.from && applied.to ? formatRange(applied.from, applied.to) : t("reportingPeriod.title");

  function handleOpenChange(nextOpen: boolean): void {
    if (nextOpen) {
      setDraft(toDateRange(value, timeZone));
      setDraftPreset(value.type === "preset" ? value.value : null);
      setDraftCompare(compare?.value);
    }
    setOpen(nextOpen);
  }

  function selectPreset(preset: ReportingPeriodPreset): void {
    setDraft(toDateRange({ type: "preset", value: preset }, timeZone));
    setDraftPreset(preset);
  }

  function selectCalendarDay(day: Date): void {
    setDraftPreset(null);
    if (!draft.from || draft.to || day < draft.from) {
      setDraft({ from: day, to: undefined });
      return;
    }
    setDraft({ from: draft.from, to: day });
  }

  function applyDraft(): void {
    if (!draft.from || !draft.to) return;
    onValueChange(
      draftPreset
        ? { type: "preset", value: draftPreset }
        : { type: "custom", startDate: toIsoDate(draft.from), endDate: toIsoDate(draft.to) },
    );
    if (compare && draftCompare !== undefined && draftCompare !== compare.value) {
      compare.onValueChange(draftCompare);
    }
    setOpen(false);
  }

  const draftRange =
    draft.from && draft.to
      ? { startDate: toIsoDate(draft.from), endDate: toIsoDate(draft.to) }
      : null;
  const compareRange =
    compare && draftRange && draftCompare !== undefined
      ? compare.resolveRange(draftRange, draftCompare)
      : null;
  const compareFrom = compareRange ? parseISO(compareRange.startDate) : undefined;
  const compareTo = compareRange ? parseISO(compareRange.endDate) : undefined;
  const disabledDays = outOfRangeDays(draft.from && !draft.to ? draft.from : undefined, today);

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <PopoverPrimitive.Trigger
        aria-label={`${t("reportingPeriod.title")}: ${appliedLabel}`}
        title={appliedLabel}
        className={reportingFilterTriggerClassName}
      >
        <FilterTriggerIcon icon={IconCalendar} />
        <span className="min-w-0 truncate tabular-nums">{appliedLabel}</span>
        <FilterTriggerIcon icon={IconChevronDown} chevron />
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Positioner
          side="bottom"
          align={align}
          sideOffset={4}
          className="isolate z-50"
        >
          <PopoverPrimitive.Popup className="popup-motion @container w-[652px] max-w-(--available-width) overflow-hidden rounded-md bg-popover text-popover-foreground shadow-[var(--paper-flyout-shadow)] outline-none">
            <PopoverPrimitive.Title className="sr-only">
              {t("reportingPeriod.title")}
            </PopoverPrimitive.Title>
            <div className="grid grid-cols-[140px_minmax(0,1fr)]">
              <div className="flex flex-col gap-0.5 border-r border-line p-1">
                {TOOLBAR_PERIOD_PRESETS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    aria-pressed={draftPreset === preset}
                    onClick={() => selectPreset(preset)}
                    className="flex h-7 shrink-0 cursor-pointer items-center truncate rounded-xs px-2.5 text-left text-sm text-text-secondary outline-none transition-colors hover:bg-state-hover hover:text-ink focus-visible:ring-2 focus-visible:ring-focus-ring aria-pressed:bg-control-highlight aria-pressed:font-medium aria-pressed:text-ink"
                  >
                    {presetLabels[preset]}
                  </button>
                ))}
                {customPresetLabel ? (
                  <span
                    aria-current={draftPreset === null ? "true" : undefined}
                    className="mt-auto flex h-7 shrink-0 items-center truncate rounded-xs px-2.5 text-sm text-text-secondary opacity-40 aria-current:bg-control-highlight aria-current:font-medium aria-current:text-ink aria-current:opacity-100"
                  >
                    {customPresetLabel}
                  </span>
                ) : null}
              </div>
              <div className="min-w-0 overflow-x-auto px-3 py-2.5">
                <Calendar
                  mode="range"
                  selected={draft}
                  // Keep DayPicker controlled by the same two-click draft as the default picker.
                  onSelect={(_range, day, modifiers) => {
                    if (!modifiers.disabled) selectCalendarDay(day);
                  }}
                  numberOfMonths={2}
                  defaultMonth={subDays(draft.to ?? draft.from ?? today, 31)}
                  weekStartsOn={1}
                  showOutsideDays={false}
                  today={today}
                  disabled={disabledDays}
                  locale={locale}
                  modifiers={{
                    monthStart: (day: Date) => day.getDate() === 1,
                    monthEnd: (day: Date) => isLastDayOfMonth(day),
                    ...(compareFrom && compareTo
                      ? {
                          compare: { from: compareFrom, to: compareTo },
                          compareStart: compareFrom,
                          compareEnd: compareTo,
                          compareEdge: [compareFrom, compareTo],
                        }
                      : {}),
                  }}
                  modifiersClassNames={TOOLBAR_CALENDAR_MODIFIER_CLASS_NAMES}
                  className="mx-auto p-0 [--cell-size:--spacing(8)] sm:[--cell-size:--spacing(8)]"
                  classNames={TOOLBAR_CALENDAR_CLASS_NAMES}
                />
              </div>
            </div>
            <div className="flex items-end justify-between gap-3 border-t border-line py-3 pr-3 pl-3.5">
              <div className="grid min-w-0 gap-2">
                <div className="flex min-w-0 items-baseline gap-2">
                  <span className="truncate text-sm font-medium tabular-nums text-ink">
                    {draft.from && draft.to
                      ? formatRange(draft.from, draft.to)
                      : draft.from
                        ? `${rangeFormat.format(draft.from)} –`
                        : "—"}
                  </span>
                  {draft.from && draft.to ? (
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                      {dayCountFormat.format(differenceInCalendarDays(draft.to, draft.from) + 1)}
                    </span>
                  ) : null}
                </div>
                {compare && draftCompare !== undefined ? (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{compare.label}</span>
                      <SegmentControl<C>
                        size="compact"
                        aria-label={compare.label}
                        value={draftCompare}
                        onValueChange={(next) => setDraftCompare(next)}
                        className="bg-control-subtle"
                      >
                        {compare.options.map((option) => (
                          <SegmentControlItem<C>
                            key={option.value}
                            value={option.value}
                            disabled={option.disabled}
                            className="text-xs"
                          >
                            {option.label}
                          </SegmentControlItem>
                        ))}
                      </SegmentControl>
                    </div>
                    {compareFrom && compareTo ? (
                      <div className="flex items-center gap-1.5">
                        <span
                          aria-hidden
                          className="h-2 w-3.5 shrink-0 rounded-[3px] bg-subtle shadow-[inset_0_0_0_1px_var(--color-control-border-hover)]"
                        />
                        <span className="text-xs tabular-nums text-muted-foreground">
                          {compare.describe(formatRange(compareFrom, compareTo))}
                        </span>
                      </div>
                    ) : null}
                  </>
                ) : null}
              </div>
              <div className="flex shrink-0 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleOpenChange(false)}
                >
                  {t("reportingPeriod.cancel")}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  disabled={!draft.from || !draft.to}
                  onClick={applyDraft}
                >
                  {t("reportingPeriod.apply")}
                </Button>
              </div>
            </div>
          </PopoverPrimitive.Popup>
        </PopoverPrimitive.Positioner>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
